import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { Loader, useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { CustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { MLRewriteService } from 'ml-capability-rewrite';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { FormModalPopover } from 'ml-components-platform-react';
import { PolicyService } from 'gw-capability-gateway';
import { MLPortalStorageService } from 'ml-services-common-react'
import { MLLocalDateUtil } from 'ml-portals-util-js';

import useCheckRewriteSteps from './util/useCheckRewriteSteps';
import Rewrite from './model/Rewrite';
import messages from './RewriteWizard.messages';
import wizardConfig from './config/rewrite-wizard-config.json5';


const ML_STORAGE_KEY_SHOW_WV_REINSTATE_MSG = 'SHOW_WV_REINSTATE_MSG';
function RewriteWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const checkRewriteSteps = useCheckRewriteSteps();

    const ModalService = MLModalService();


    const isCancelledBetween7and46Days = (policyDetails) => {
        return (Math.abs(MLLocalDateUtil.differenceInDays(MLLocalDateUtil.localDate2Date(policyDetails.cancelledDate_ML), new Date())) > 7
            && Math.abs(MLLocalDateUtil.differenceInDays(MLLocalDateUtil.localDate2Date(policyDetails.cancelledDate_ML), new Date())) < 46);
    };

    const showWVPaymentMsg = (policies) => {
        return policies && policies.some((policy) => policy.isCancelled
            && isCancelledBetween7and46Days(policy) && policy.address_ML.state === 'WV');
    };

    useEffect( () => {
        if (!location.state) {
            history.push('/');
            return;
        }
        const {
            state: { rewriteData }
        } = location;
        if (!rewriteData) {
            return;
        }
        
        const promise = MLRewriteService.retrieveRewrite(rewriteData.policy.policyNumber, authHeader);
        ModalService.showProgressDialog(messages.retrieving, promise);
        promise.then((result) => {
            const submissionVM = viewModelService.create(
                new Rewrite(result),
                'pc', 
                'metlife.edge.capabilities.gateway.job.rewrite.dto.RewriteDTOML'
            );
            setInitialSubmission(submissionVM);
            checkRewriteSteps.checkStepsAtWizard(submissionVM, steps);

            PolicyService.getPolicySummariesFromPolicyNumbers([submissionVM.policy.policyNumber.value], authHeader)
                .then((policySummaries)=> {
                    let showWVMsg;
                    if (policySummaries && policySummaries.length) {
                        showWVMsg = {'shouldShow': false};
                        showWVMsg.shouldShow = showWVPaymentMsg(policySummaries);
                    }
                    MLPortalStorageService.store(null, submissionVM.policy.account.accountNumber.value,
                        ML_STORAGE_KEY_SHOW_WV_REINSTATE_MSG, JSON.stringify(showWVMsg), authHeader);

                });
            setIsLoading(false);
        });
        promise.catch((error) => {
            error.modalTitle = translator(messages.errorRetrievingTitle)
            error.modalMessage = translator(messages.errorRetrievingMessage)
            throw error;
        })
        return () => {
            MLTagManagerService.setModel();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleOnCancel = useCallback((wizardProps) => {
        const {currentStepIndex, wizardData, param: nextPath = ''} = wizardProps;
        const currentStep = steps[currentStepIndex].mlStepName;
        if(currentStep === 'rewriteConfirmation')  return true;
        if(nextPath.indexOf('rewriteContactUs') > -1)  return true;

        const componentProps = {
            title: translator(messages.cancelPromptTitle),
            actionBtnLabel: platformMessages.yes,
            cancelBtnLabel: platformMessages.cancelModel,
            simpleMessage: translator(messages.cancelPrompt),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {

            const jobNumber = _.get(wizardData, 'submissionVM.jobNumber.value');
            if(jobNumber){
                const promise = MLRewriteService.withdrawRewriteByJobNumber(jobNumber, authHeader).then(() => {
                    setIsLoading(true);
                    history.push(nextPath);
                    return true;
                }).catch((error) => {
                    error.modalTitle = translator(messages.withdrawErrorTitle);
                    error.modalMessage = translator(messages.withdrawErrorMessage);
                    throw error;
                });
                ModalService.showProgressDialog(messages.withdrawing, promise);
                return promise;
            }
            setIsLoading(true);
            history.push(nextPath);
            return true;
        }, _.noop);
        // US946510 avoid showing modal twice (withdraw error - no active period)
    }, []);

    const handleError = useCallback((error) => {
        console.error(error);
        const errorTitle = error.modalTitle ||translator(messages.genericErrorTitle);
        const errorMessage = error.modalMessage || translator(messages.genericErrorMessage);
        const componentProps = {
            title: translator(errorTitle),
            actionBtnLabel: platformMessages.ok,
            simpleMessage: translator(errorMessage),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            return history.push('/rewriteContactUs', error);
        }, _.noop);
    }, [history, translator]);


    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <QuoteInfoComponentContext.Provider value={CustomQuoteInfoComponent}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    initialData={{
                        submissionVM: initialSubmission,
                        isRewrite: true
                    }}
                    onCancel={handleOnCancel}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'mi-error-outline',
                        confirmButtonText: platformMessages.yes,
                        cancelButtonText: platformMessages.no
                    }}
                    extractVMObject={(data) => _.get(data, 'submissionVM')}
                />
            </ErrorBoundary>
        </QuoteInfoComponentContext.Provider>
    );
}

export default withRouter(withViewModelService(RewriteWizard));
